// Data models for startup opportunities and connection requests

/**
 * Generates a unique ID (placeholder - in real implementation would use Firestore auto-generated IDs)
 * @returns {string} - A unique identifier
 */
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * StartupOpportunity data model class
 */
export class StartupOpportunity {
  constructor({
    companyId = '',
    companyName,
    companyDescription,
    fundingStage,
    fundingAmount = '',
    leadInvestors = [],
    roleArea,
    roleDescription,
    experienceLevel,
    isFlexible = true,
    highlights = [],
    founderInfo = null,
    companyMetrics = null,
    requirements = [],
    benefits = [],
    applicationProcess = '',
    status = 'active'
  }) {
    // Validate required fields
    if (!companyName || !companyDescription || !roleArea || !roleDescription || !fundingStage || !experienceLevel) {
      throw new Error('Missing required fields for StartupOpportunity');
    }

    this.id = generateId();
    this.companyId = companyId;
    this.companyName = companyName;
    this.companyDescription = companyDescription;
    this.fundingStage = fundingStage;
    this.fundingAmount = fundingAmount;
    this.leadInvestors = Array.isArray(leadInvestors) ? leadInvestors : [];
    this.roleArea = roleArea;
    this.roleDescription = roleDescription;
    this.experienceLevel = experienceLevel;
    this.isFlexible = isFlexible;
    this.highlights = Array.isArray(highlights) ? highlights : [];
    this.founderInfo = founderInfo;
    this.companyMetrics = companyMetrics;
    this.requirements = Array.isArray(requirements) ? requirements : [];
    this.benefits = Array.isArray(benefits) ? benefits : [];
    this.applicationProcess = applicationProcess;
    this.status = status;
    this.createdAt = new Date();
    this.updatedAt = new Date();
  }

  /**
   * Convert to plain object for Firestore storage
   * @returns {Object} - Plain object representation
   */
  toFirestore() {
    return {
      companyId: this.companyId,
      companyName: this.companyName,
      companyDescription: this.companyDescription,
      fundingStage: this.fundingStage,
      fundingAmount: this.fundingAmount,
      leadInvestors: this.leadInvestors,
      roleArea: this.roleArea,
      roleDescription: this.roleDescription,
      experienceLevel: this.experienceLevel,
      isFlexible: this.isFlexible,
      highlights: this.highlights,
      founderInfo: this.founderInfo,
      companyMetrics: this.companyMetrics,
      requirements: this.requirements,
      benefits: this.benefits,
      applicationProcess: this.applicationProcess,
      status: this.status,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  /**
   * Create instance from Firestore data
   * @param {string} id - Document ID
   * @param {Object} data - Firestore document data
   * @returns {StartupOpportunity} - New instance
   */
  static fromFirestore(id, data) {
    const opportunity = new StartupOpportunity(data);
    opportunity.id = id;
    return opportunity;
  }

  /**
   * Validate opportunity data
   * @returns {Object} - Validation result
   */
  validate() {
    const errors = [];

    if (!this.companyName || this.companyName.trim() === '') {
      errors.push('Company name is required');
    }

    if (!this.companyDescription || this.companyDescription.trim() === '') {
      errors.push('Company description is required');
    }

    if (!this.roleArea || this.roleArea.trim() === '') {
      errors.push('Role area is required');
    }

    if (!this.roleDescription || this.roleDescription.trim() === '') {
      errors.push('Role description is required');
    }

    if (!this.fundingStage || this.fundingStage.trim() === '') {
      errors.push('Funding stage is required');
    }

    if (!this.experienceLevel || this.experienceLevel.trim() === '') {
      errors.push('Experience level is required');
    }

    // Validate founder info if provided
    if (this.founderInfo) {
      if (!this.founderInfo.name || this.founderInfo.name.trim() === '') {
        errors.push('Founder name is required when founder info is provided');
      }
      if (!this.founderInfo.title || this.founderInfo.title.trim() === '') {
        errors.push('Founder title is required when founder info is provided');
      }
    }

    // Validate status
    const validStatuses = ['active', 'paused', 'filled'];
    if (!validStatuses.includes(this.status)) {
      errors.push('Status must be one of: active, paused, filled');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * ConnectionRequest data model class
 */
export class ConnectionRequest {
  constructor({
    userId,
    opportunityId,
    userMessage,
    adminNotes = '',
    status = 'pending'
  }) {
    // Validate required fields
    if (!userId || !opportunityId || !userMessage) {
      throw new Error('Missing required fields for ConnectionRequest');
    }

    this.id = generateId();
    this.userId = userId;
    this.opportunityId = opportunityId;
    this.userMessage = userMessage;
    this.adminNotes = adminNotes;
    this.status = status;
    this.createdAt = new Date();
    this.processedAt = null;
    this.processedBy = null;
  }

  /**
   * Convert to plain object for Firestore storage
   * @returns {Object} - Plain object representation
   */
  toFirestore() {
    return {
      userId: this.userId,
      opportunityId: this.opportunityId,
      userMessage: this.userMessage,
      adminNotes: this.adminNotes,
      status: this.status,
      createdAt: this.createdAt,
      processedAt: this.processedAt,
      processedBy: this.processedBy
    };
  }

  /**
   * Create instance from Firestore data
   * @param {string} id - Document ID
   * @param {Object} data - Firestore document data
   * @returns {ConnectionRequest} - New instance
   */
  static fromFirestore(id, data) {
    const request = new ConnectionRequest(data);
    request.id = id;
    request.processedAt = data.processedAt;
    request.processedBy = data.processedBy;
    return request;
  }

  /**
   * Validate connection request data
   * @returns {Object} - Validation result
   */
  validate() {
    const errors = [];

    if (!this.userId || this.userId.trim() === '') {
      errors.push('User ID is required');
    }

    if (!this.opportunityId || this.opportunityId.trim() === '') {
      errors.push('Opportunity ID is required');
    }

    if (!this.userMessage || this.userMessage.trim() === '') {
      errors.push('User message is required');
    }

    // Validate message length
    if (this.userMessage && this.userMessage.length > 1000) {
      errors.push('User message must be 1000 characters or less');
    }

    // Validate status
    const validStatuses = ['pending', 'approved', 'introduced', 'declined'];
    if (!validStatuses.includes(this.status)) {
      errors.push('Status must be one of: pending, approved, introduced, declined');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Mark request as processed
   * @param {string} processedBy - ID of admin who processed the request
   * @param {string} newStatus - New status for the request
   */
  markAsProcessed(processedBy, newStatus) {
    const validProcessedStatuses = ['approved', 'introduced', 'declined'];
    if (!validProcessedStatuses.includes(newStatus)) {
      throw new Error('Invalid processed status');
    }

    this.status = newStatus;
    this.processedAt = new Date();
    this.processedBy = processedBy;
  }
}

/**
 * Utility functions for working with startup opportunities
 */
export const StartupOpportunityUtils = {
  /**
   * Get valid funding stages
   * @returns {string[]} - Array of valid funding stages
   */
  getFundingStages() {
    return [
      'pre-seed',
      'seed',
      'series-a',
      'series-b',
      'series-c',
      'series-d+',
      'growth',
      'ipo-ready'
    ];
  },

  /**
   * Get valid role areas
   * @returns {string[]} - Array of valid role areas
   */
  getRoleAreas() {
    return [
      'engineering',
      'product',
      'design',
      'sales',
      'marketing',
      'operations',
      'finance',
      'legal',
      'hr',
      'customer-success',
      'business-development',
      'data-science',
      'security',
      'other'
    ];
  },

  /**
   * Get valid experience levels
   * @returns {string[]} - Array of valid experience levels
   */
  getExperienceLevels() {
    return [
      'entry-level',
      '1-3-years',
      '3-5-years',
      '5-8-years',
      '8-12-years',
      '12+-years',
      'executive'
    ];
  },

  /**
   * Format funding stage for display
   * @param {string} stage - Funding stage
   * @returns {string} - Formatted stage
   */
  formatFundingStage(stage) {
    const stageMap = {
      'pre-seed': 'Pre-Seed',
      'seed': 'Seed',
      'series-a': 'Series A',
      'series-b': 'Series B',
      'series-c': 'Series C',
      'series-d+': 'Series D+',
      'growth': 'Growth',
      'ipo-ready': 'IPO Ready'
    };
    return stageMap[stage] || stage;
  },

  /**
   * Format role area for display
   * @param {string} area - Role area
   * @returns {string} - Formatted area
   */
  formatRoleArea(area) {
    const areaMap = {
      'engineering': 'Engineering',
      'product': 'Product',
      'design': 'Design',
      'sales': 'Sales',
      'marketing': 'Marketing',
      'operations': 'Operations',
      'finance': 'Finance',
      'legal': 'Legal',
      'hr': 'Human Resources',
      'customer-success': 'Customer Success',
      'business-development': 'Business Development',
      'data-science': 'Data Science',
      'security': 'Security',
      'other': 'Other'
    };
    return areaMap[area] || area;
  },

  /**
   * Format experience level for display
   * @param {string} level - Experience level
   * @returns {string} - Formatted level
   */
  formatExperienceLevel(level) {
    const levelMap = {
      'entry-level': 'Entry Level',
      '1-3-years': '1-3 Years',
      '3-5-years': '3-5 Years',
      '5-8-years': '5-8 Years',
      '8-12-years': '8-12 Years',
      '12+-years': '12+ Years',
      'executive': 'Executive'
    };
    return levelMap[level] || level;
  }
};

/**
 * Utility functions for working with connection requests
 */
export const ConnectionRequestUtils = {
  /**
   * Get valid connection request statuses
   * @returns {string[]} - Array of valid statuses
   */
  getStatuses() {
    return ['pending', 'approved', 'introduced', 'declined'];
  },

  /**
   * Format status for display
   * @param {string} status - Request status
   * @returns {string} - Formatted status
   */
  formatStatus(status) {
    const statusMap = {
      'pending': 'Pending Review',
      'approved': 'Approved',
      'introduced': 'Introduction Made',
      'declined': 'Declined'
    };
    return statusMap[status] || status;
  },

  /**
   * Get status color for UI
   * @param {string} status - Request status
   * @returns {string} - CSS color class or color code
   */
  getStatusColor(status) {
    const colorMap = {
      'pending': 'text-yellow-600',
      'approved': 'text-blue-600',
      'introduced': 'text-green-600',
      'declined': 'text-red-600'
    };
    return colorMap[status] || 'text-gray-600';
  }
};